import type { SongModel } from "@/models/interfaces";

/**
 * The languages a song can be filtered by. A song stores its own as
 * `language`, null when its words tell neither; a song without the field has
 * not been looked at yet (see `songCatalogue/detectLanguages`).
 */
export type SongLanguage = "da" | "en";

export const SONG_LANGUAGES: SongLanguage[] = ["da", "en"];

/** The locale key of a language's name. */
export const songLanguageLabel: Record<SongLanguage, string> = {
  da: "Danish",
  en: "English",
};

export const isSongLanguage = (value: unknown): value is SongLanguage =>
  SONG_LANGUAGES.includes(value as SongLanguage);

// Words that are common in one language's worship songs and rare in the
// other's. Tried on the test organisation's 147 songs, every one with lyrics
// came out right, most by a wide margin.
const DANISH = new Set(
  (
    "og jeg du er det en at til min din mig dig han som på med af ikke vi os " +
    "vor vores har vil skal kan hans fra alt alle her nu så mit dit hvor hvad " +
    "den de dem hele jer jeres ved over under når hjerte herre gud kærlighed " +
    "nåde ære lov evig evigt aldrig altid"
  ).split(" "),
);
const ENGLISH = new Set(
  (
    "the and you i my your is are to of in it we our he his me for with be " +
    "all will on that this who what love grace lord god forever never always " +
    "there no not can so oh praise holy name heart"
  ).split(" "),
);

/**
 * Tells Danish from English by a song's title and words, or null when they
 * say too little either way. Chord tabs are left out, as are the chords a
 * legacy chapter keeps inline (`[{"primary":1}]`).
 */
export function detectSongLanguage(
  song: Pick<SongModel, "title"> & { data?: unknown },
): SongLanguage | null {
  const chapters = Array.isArray(song.data)
    ? song.data
    : song.data && typeof song.data === "object"
      ? Object.values(song.data)
      : [];
  const text = [
    song.title ?? "",
    ...chapters.map((chapter) =>
      chapter?.type === "tabs" ? "" : String(chapter?.content ?? ""),
    ),
  ]
    .join(" ")
    .replace(/\[[^\]]*\]/g, " ")
    .replaceAll("|n", " ")
    .toLowerCase();

  // æ, ø and å are Danish whatever the word.
  let danish = (text.match(/[æøå]/g)?.length ?? 0) / 2;
  let english = 0;
  for (const word of text.match(/[a-zæøå']+/g) ?? []) {
    if (DANISH.has(word)) danish++;
    if (ENGLISH.has(word)) english++;
  }
  if (danish > english) return "da";
  if (english > danish) return "en";
  return null;
}
